import React, { useState } from "react";
import PropTypes from "prop-types";
import { resolve } from "styled-jsx/css";
import { Tags } from "../Tags";
import { Card } from "../Card";
import { Image } from "../Image";
import { ThemeProvider, useTheme } from "../theme";
import HeadingIcon from "./popular-articles-heading-icon.png";
import { SmartList } from "../List";
import { PopularArticle } from "./PopularArticle";
import { Disclosure, DisclosureButton, DisclosurePanel } from "../Disclosure";
import CollapseIcon from "./whats-hot-collapse-icon.svg";
import ExpandIcon from "./whats-hot-expand-icon.svg";

const getPopularArticles = (articles) => articles.slice(0, 4);

const getTags = (articles) => {
    return Object.values(
        articles.reduce((acc, { tags }) => {
            if (!tags?.length) {
                return acc;
            }
            return {
                ...acc,
                ...tags.reduce((tagsObj, tag) => {
                    const { slug } = tag;
                    tagsObj[slug] = tag;
                    return tagsObj;
                }, {}),
            };
        }, {})
    );
};

export function PopularArticlesCard({ allArticles }) {
    const popularArticles = getPopularArticles(allArticles);
    const popularTags = getTags(popularArticles);
    const { cardGridReflowBreakpoint = "0em" } = useTheme();

    return (
        <div className="popular-articles-card">
            <PopularArticlesCollapsible
                className="popular-articles--collapsible"
                articles={popularArticles}
                tags={popularTags}
            />
            <PopularArticles
                className="popular-articles"
                articles={popularArticles}
                tags={popularTags}
            />
            <style jsx>{`
                .popular-articles-card :global(.popular-articles) {
                    display: none;
                }

                @media (min-width: ${cardGridReflowBreakpoint}) {
                    .popular-articles-card
                        :global(.popular-articles--collapsible) {
                        display: none;
                    }
                    .popular-articles-card :global(.popular-articles) {
                        display: block;
                    }
                }
            `}</style>
        </div>
    );
}

PopularArticlesCard.propTypes = {
    allArticles: PropTypes.array.isRequired,
};

function PopularArticlesCollapsible({ className, articles, tags }) {
    const [open, setOpen] = useState(false);
    const toggleContent = (e) => {
        e.stopPropagation();
        setOpen((isOpen) => !isOpen);
    };

    return (
        <Card className={className}>
            <Disclosure id="popular-articles" open={open}>
                <PopularArticlesCardHeader onClick={toggleContent}>
                    <DisclosureButton as={CardHeaderButton} />
                </PopularArticlesCardHeader>
                <DisclosurePanel>
                    <PopularArticlesCardContent
                        popularArticles={articles}
                        popularTags={tags}
                    />
                </DisclosurePanel>
            </Disclosure>
        </Card>
    );
}

function PopularArticles({ className, articles, tags }) {
    return (
        <Card className={className}>
            <PopularArticlesCardHeader />
            <PopularArticlesCardContent
                popularArticles={articles}
                popularTags={tags}
            />
        </Card>
    );
}

function CardHeaderButton({ className, ...rest }) {
    const { "aria-expanded": isExpanded } = rest;
    const { altTextColor } = useTheme();
    return (
        <button
            className={`card-header-button ${className}`}
            aria-label="Show"
            {...rest}
        >
            <span className="card-header-button__content">
                <Image
                    aria-hidden
                    className="card-header-button__image"
                    src={isExpanded ? CollapseIcon : ExpandIcon}
                    alt=""
                />
            </span>
            <style jsx>{`
                .card-header-button {
                    background-color: transparent;
                    cursor: pointer;
                    padding: 1em;
                }
                .card-header-button :global(.card-header-button__content) {
                    display: block;
                    height: 1.2em;
                    width: 1.2em;
                    padding: 0.2em;
                    font-size: 1.5em;
                    line-height: 1;
                    border: 1px solid ${altTextColor};
                    border-radius: 50%;

                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                .card-header-button :global(.card-header-button__image) {
                    width: 100%;
                }
                .card-header-button:hover .card-header-button__content,
                .card-header-button:focus .card-header-button__content {
                    background-color: ${altTextColor};
                }
                .card-header-button:hover :global(.card-header-button__image),
                .card-header-button:focus :global(.card-header-button__image) {
                    filter: invert(1) contrast(200%);
                }
            `}</style>
        </button>
    );
}

function PopularArticlesCardHeader({ children, onClick }) {
    const { highlightBackgroundColor } = useTheme();
    return (
        <header
            className={`popular-articles-card__header ${
                onClick ? "popular-articles-card__header--clickable" : ""
            }`}
            onClick={onClick}
        >
            <Image
                aria-hidden
                className="popular-articles-card__header-icon"
                src={HeadingIcon}
                alt="icon"
            />
            <h3 className="override-global-styles popular-articles-card__heading">
                What's Hot
            </h3>
            {children && (
                <div className="popular-articles-card__header-additional">
                    {children}
                </div>
            )}
            <style jsx>{`
                .popular-articles-card__header {
                    background-color: ${highlightBackgroundColor};
                    padding: 1.5em 1.5em;
                    margin-left: -1.5em;

                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                }
                .popular-articles-card__header--clickable {
                    cursor: pointer;
                }
                .popular-articles-card__header
                    :global(.popular-articles-card__header-icon) {
                    display: none;
                }
                .popular-articles-card__heading.override-global-styles {
                    display: inline-block;
                    vertical-align: middle;
                    margin-left: 1em;

                    flex: 1 1 auto;
                }
                .popular-articles-card__header-additional {
                    display: inline-block;
                    vertical-align: middle;
                    margin-left: 1.5em;
                }
                @media (min-width: 25em) {
                    .popular-articles-card__header
                        :global(.popular-articles-card__header-icon) {
                        display: inline-block;
                        vertical-align: middle;
                        margin-left: 1.5em;
                        max-width: 4em;
                    }
                }
            `}</style>
        </header>
    );
}

// Use resolve so we do not need a wrapper DOM element for scoped styles
const { className: articleClassName, styles: articleStyles } = resolve`
    padding: 1.5em 0;
    margin: 0 1.5em;
    border-bottom: 1px solid #e2e5e8;
    list-style-type: none;

    :last-child {
        border-bottom: none;
    }
`;

function PopularArticlesCardContent({ popularArticles, popularTags }) {
    const tagTheme = {
        tagBackgroundColor: "#ffffff",
    };
    const { highlightBackgroundColor } = useTheme();

    return (
        <>
            <SmartList
                itemClassName={articleClassName}
                items={popularArticles.map(
                    (
                        { link, title, heroImage, author, publishedDate },
                        key
                    ) => ({
                        key,
                        display: (
                            <PopularArticle
                                title={title}
                                heroImage={heroImage}
                                link={link}
                                author={author}
                                publishedDate={publishedDate}
                            />
                        ),
                    })
                )}
            />
            {!!popularTags.length && (
                <footer className="popular-articles-content__footer">
                    <h3>Hot Topics</h3>
                    <ThemeProvider theme={tagTheme}>
                        <Tags
                            className="popular-articles-card__tags"
                            tags={popularTags}
                        />
                    </ThemeProvider>
                </footer>
            )}
            {articleStyles}
            <style jsx>{`
                .popular-articles-content__footer {
                    padding: 1.5em;
                    background-color: ${highlightBackgroundColor};
                }

                .popular-articles-content__footer
                    :global(.popular-articles-card__tags) {
                    margin-top: 1em;
                }
            `}</style>
        </>
    );
}
